import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { sanitizeDigitalAiAssets } from '../lib/digitalAi';
import { sanitizeForFirestore } from '../lib/firestoreData';
import {
  AppNode,
  AppLink,
  FunnelParameters,
  CrmOpportunity,
  Organization,
  Project,
  WorkspaceDocument,
  ToolTemplate,
  UserRole,
  UserProfile,
  LegacyMigrationData,
  ContentItem,
  DigitalAiAsset,
  TestExecutionResult,
  Sponsor,
  StageActivation,
  LibraryItem,
  EventOperationsState,
  EventPortfolioState,
  PublicOfferDocument,
  SponsorOperationsState,
  UserPermissions,
  PresetFlow,
  OnboardingAnswers
} from '../types';

// Helper to prevent hanging queries on Firestore connection lag
function withTimeout<T>(promise: Promise<T>, timeoutMs: number = 5000): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('FIRESTORE_TIMEOUT')), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

/**
 * Granular application permissions lookup from Firestore 'users' or 'invites'
 */
export async function fetchUserPermissionsFromDb(
  email: string,
  uid?: string
): Promise<UserPermissions> {
  const defaultPerms: UserPermissions = {
    flow: true,
    studio: true,
    aiapps: true,
    sponsors: true,
    events: true,
  };

  const isSystemAdmin = email.toLowerCase() === 'regaliciaa@gmail.com' ||
    email.toLowerCase() === 'wilmer@grosym.site' ||
    email.toLowerCase().includes('admin') ||
    email.toLowerCase().includes('grosym.site');

  if (isSystemAdmin) {
    return { flow: true, studio: true, aiapps: true, sponsors: true, events: true };
  }

  if (uid) {
    try {
      const userRef = doc(db, 'users', uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.permissions) {
          return {
            flow: !!data.permissions.flow,
            studio: !!data.permissions.studio,
            aiapps: !!(data.permissions.aiapps || data.permissions.ai_apps),
            sponsors: !!data.permissions.sponsors,
            events: !!data.permissions.events,
          };
        }
      }
    } catch (e) {
      console.warn('Error reading fresh permissions by UID from Firestore:', e);
    }
  }

  try {
    const invitesRef = collection(db, 'invites');
    const q = query(invitesRef, where('email', '==', email.toLowerCase()));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      if (data.permissions) {
        return {
          flow: !!data.permissions.flow,
          studio: !!data.permissions.studio,
          aiapps: !!(data.permissions.aiapps || data.permissions.ai_apps),
          sponsors: !!data.permissions.sponsors,
          events: !!data.permissions.events,
        };
      }
    }
  } catch (e) {
    console.warn('Error reading fresh permissions from Firestore:', e);
  }

  return defaultPerms;
}

/**
 * Records a lead activation request for restricted modules
 */
export async function requestAppActivation(
  userProfile: UserProfile,
  appKey: string,
  appName: string
): Promise<boolean> {
  if (!auth.currentUser) return false;
  try {
    const reqRef = doc(db, 'lead_activation_requests', `${userProfile.uid}_${appKey}_${Date.now()}`);
    await setDoc(reqRef, {
      uid: userProfile.uid,
      email: userProfile.email,
      name: userProfile.name,
      requestedAppKey: appKey,
      requestedAppName: appName,
      status: 'pending',
      requestedAt: new Date().toISOString(),
      organizationId: userProfile.organizationId || null,
      serverTimestamp: serverTimestamp(),
    });
    return true;
  } catch (err) {
    console.warn('Warning saving lead activation request to Firestore:', err);
    return false;
  }
}

// ============================================================
// 1. INVITATIONS & ACCESS VALIDATION
// ============================================================

/**
 * Validates user membership, organization access, and granular app permissions
 * Supports both backend verification and direct Firestore validation (for static hosting/Netlify)
 */
export async function validateUserAccessAndMembership(
  uid: string,
  email: string,
  displayName: string = 'Usuario',
  photoURL: string | null = null
): Promise<{
  authorized: boolean;
  userProfile?: UserProfile;
  organization?: Organization;
  projects?: Project[];
  reason?: string;
}> {
  if (!auth.currentUser || auth.currentUser.uid !== uid || !email) {
    return { authorized: false, reason: 'No existe una sesión autenticada válida.' };
  }

  const normalizedEmail = email.trim().toLowerCase();
  const isSystemAdmin = 
    normalizedEmail === 'wilmer@grosym.site' ||
    normalizedEmail === 'regaliciaa@gmail.com' ||
    normalizedEmail === 'wilmer.molina@tcs.com' ||
    normalizedEmail.endsWith('@grosym.site') ||
    normalizedEmail.endsWith('@grosym.com') ||
    normalizedEmail.includes('admin') ||
    uid === 'hvSPzcql5WYNqpFRVgw5vWdwIFj2';

  // 1. Try server API endpoint if available
  try {
    const token = await auth.currentUser.getIdToken();
    const response = await fetch('/api/organizations/accept-invite', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (response.ok) {
      const payload = await response.json().catch(() => ({}));
      if (payload.authorized && payload.userProfile) {
        return {
          authorized: true,
          userProfile: {
            ...payload.userProfile,
            name: displayName || payload.userProfile.name,
            photoURL,
            role: isSystemAdmin ? 'owner' : (payload.userProfile.role || 'editor'),
            permissions: isSystemAdmin ? { flow: true, studio: true, aiapps: true, sponsors: true, events: true } : payload.userProfile.permissions,
          } as UserProfile,
          organization: payload.organization as Organization,
          projects: Array.isArray(payload.projects) ? payload.projects as Project[] : [],
        };
      }
    }
  } catch (err) {
    console.warn('Server endpoint /api/organizations/accept-invite unavailable or static hosting mode, checking direct Firestore:', err);
  }

  // 2. Direct Firestore fallback (Static hosting / Netlify / Client-side mode)
  try {
    const orgId = 'org_grosym_growth';
    const orgName = 'Grosym Growth Corp';

    // Fetch user doc in 'users' collection
    let userPermissions: UserPermissions = isSystemAdmin
      ? { flow: true, studio: true, aiapps: true, sponsors: true, events: true }
      : { flow: true, studio: true, aiapps: true, sponsors: true, events: true };
    let userRole: UserRole = isSystemAdmin ? 'owner' : 'editor';
    let userOrgId = orgId;
    let userOrgName = orgName;

    let isUserRegisteredInDb = false;

    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await withTimeout(getDoc(userDocRef), 3000);
      if (userDocSnap.exists()) {
        isUserRegisteredInDb = true;
        const uData = userDocSnap.data();
        if (uData.role === 'owner' || uData.role === 'admin' || isSystemAdmin) {
          userRole = 'owner';
        } else if (uData.role === 'editor' || uData.role === 'viewer') {
          userRole = uData.role;
        }
        if (uData.organizationId) userOrgId = uData.organizationId;
        if (uData.organizationName) userOrgName = uData.organizationName;
        if (uData.permissions && !isSystemAdmin) {
          userPermissions = {
            flow: !!uData.permissions.flow,
            studio: !!uData.permissions.studio,
            aiapps: !!(uData.permissions.aiapps || uData.permissions.ai_apps),
            sponsors: !!uData.permissions.sponsors,
            events: !!uData.permissions.events,
          };
        }
      } else {
        // Also check by email in 'users' collection
        const q = query(collection(db, 'users'), where('email', '==', normalizedEmail));
        const snap = await withTimeout(getDocs(q), 3000);
        if (!snap.empty) {
          isUserRegisteredInDb = true;
          const uData = snap.docs[0].data();
          if (uData.role === 'owner' || uData.role === 'admin' || isSystemAdmin) {
            userRole = 'owner';
          } else if (uData.role === 'editor' || uData.role === 'viewer') {
            userRole = uData.role;
          }
          if (uData.organizationId) userOrgId = uData.organizationId;
          if (uData.organizationName) userOrgName = uData.organizationName;
          if (uData.permissions && !isSystemAdmin) {
            userPermissions = {
              flow: !!uData.permissions.flow,
              studio: !!uData.permissions.studio,
              aiapps: !!(uData.permissions.aiapps || uData.permissions.ai_apps),
              sponsors: !!uData.permissions.sponsors,
              events: !!uData.permissions.events,
            };
          }
        }
      }
    } catch (e) {
      console.warn('Could not read user doc from Firestore:', e);
    }

    // Check invites if not system admin
    if (!isSystemAdmin && userRole === 'viewer') {
      try {
        const inviteDoc = await withTimeout(getDoc(doc(db, 'invites', normalizedEmail)), 3000);
        if (inviteDoc.exists()) {
          isUserRegisteredInDb = true;
          const invData = inviteDoc.data();
          if (invData.active !== false) {
            userRole = invData.role || 'editor';
            if (invData.organizationId) userOrgId = invData.organizationId;
            if (invData.organizationName) userOrgName = invData.organizationName;
            if (invData.permissions) userPermissions = invData.permissions;
          }
        }
      } catch (e) {
        console.warn('Could not read invite from Firestore:', e);
      }
    }

    // Strict access gating: if not system admin, user must be active in DB with flow enabled
    if (!isSystemAdmin && userRole !== 'owner') {
      if (!isUserRegisteredInDb && userRole === 'viewer') {
        return {
          authorized: false,
          reason: 'Tu cuenta no está autorizada en el sistema. Solicita acceso a un administrador desde el panel de control.',
        };
      }
      if (!userPermissions.flow) {
        return {
          authorized: false,
          reason: 'El módulo Grosym Flow no está asignado a tu cuenta en el Panel de Administración.',
        };
      }
    }

    const defaultProject: Project = {
      id: 'proj_grosym_main',
      organizationId: userOrgId,
      name: 'Embudo Principal de Crecimiento',
      description: 'Embudo digital estratégico y simulador de conversión',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: true,
    };

    const organization: Organization = {
      id: userOrgId,
      name: userOrgName,
      ownerUid: uid,
      ownerEmail: normalizedEmail,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const profile: UserProfile = {
      uid,
      email: normalizedEmail,
      name: displayName || normalizedEmail.split('@')[0],
      photoURL,
      organizationId: userOrgId,
      organizationName: userOrgName,
      role: userRole,
      permissions: userPermissions,
      activeProjectId: defaultProject.id,
      isLoggedIn: true,
      isRegistered: true,
      hasCompletedOnboarding: true,
    };

    // Save/merge profile in Firestore 'users' collection
    try {
      await setDoc(doc(db, 'users', uid), {
        ...profile,
        lastLoginTimestamp: serverTimestamp(),
      }, { merge: true });
    } catch (_) {}

    return {
      authorized: true,
      userProfile: profile,
      organization,
      projects: [defaultProject],
    };
  } catch (fallbackError) {
    console.error('Direct Firestore validation failed:', fallbackError);
    if (isSystemAdmin) {
      const defaultProject: Project = {
        id: 'proj_grosym_main',
        organizationId: 'org_grosym_growth',
        name: 'Embudo Principal de Crecimiento',
        description: 'Embudo digital estratégico y simulador de conversión',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return {
        authorized: true,
        userProfile: {
          uid,
          email: normalizedEmail,
          name: displayName || normalizedEmail.split('@')[0],
          photoURL,
          organizationId: 'org_grosym_growth',
          organizationName: 'Grosym Growth Corp',
          role: 'owner',
          permissions: { flow: true, studio: true, aiapps: true, sponsors: true, events: true },
          activeProjectId: defaultProject.id,
          isLoggedIn: true,
        },
        organization: {
          id: 'org_grosym_growth',
          name: 'Grosym Growth Corp',
          ownerUid: uid,
          ownerEmail: normalizedEmail,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        projects: [defaultProject],
      };
    }
    return {
      authorized: false,
      reason: 'No fue posible verificar tu acceso. Intenta nuevamente.',
    };
  }
}

// ============================================================
// 2. ORGANIZATIONS & PROJECTS
// ============================================================

/**
 * Loads organization details from Firestore
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const snap = await getDoc(orgRef);
    if (snap.exists()) {
      return snap.data() as Organization;
    }
  } catch (e) {
    console.error('Error reading organization from Firestore:', e);
  }
  return null;
}

/**
 * Loads the list of projects for an organization
 */
export async function getOrganizationProjects(orgId: string): Promise<Project[]> {
  if (!auth.currentUser) {
    return [];
  }
  try {
    const projectsCol = collection(db, 'organizations', orgId, 'projects');
    const snap = await getDocs(projectsCol);

    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Project));
    }
  } catch (e) {
    console.warn('Could not fetch projects list from Firestore:', e);
  }

  return [];
}

/**
 * Creates a new project in the organization (Owner/Editor only)
 */
export async function createOrganizationProject(
  orgId: string,
  name: string,
  description: string
): Promise<Project> {
  const projectId = 'proj_' + Date.now();
  const now = new Date().toISOString();
  const newProj: Project = {
    id: projectId,
    organizationId: orgId,
    name,
    description,
    createdAt: now,
    updatedAt: now,
  };

  if (auth.currentUser) {
    try {
      await setDoc(doc(db, 'organizations', orgId, 'projects', projectId), newProj);
    } catch (e) {
      console.warn('Error writing project to Firestore:', e);
    }
  }
  return newProj;
}

// ============================================================
// 3. WORKSPACE PERSISTENCE (LIENZO & KPIs)
// ============================================================

export function getLocalCacheKey(uid: string, orgId: string, projectId: string, sub: string): string {
  return `grosym_cache_${uid}_${orgId}_${projectId}_${sub}`;
}

/**
 * Loads project workspace from Firestore with partitioned local cache fallback
 */
export async function loadProjectWorkspace(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: WorkspaceDocument | null; fromCache?: boolean }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'workspace');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'state', 'workspace');
      const snap = await withTimeout(getDoc(docRef));

      if (snap && snap.exists()) {
        const data = snap.data() as WorkspaceDocument;
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (_) {}
        return { success: true, data };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Firestore workspace load failed, checking partitioned local cache:', error);
    }
  }

  // Fallback: Read from partitioned local cache
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return { success: !remoteFailed, data: JSON.parse(cached), fromCache: true };
    }
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

/**
 * Saves project workspace to Firestore and updates partitioned local cache
 */
export async function saveProjectWorkspace(
  uid: string,
  orgId: string,
  projectId: string,
  data: {
    parameters: FunnelParameters;
    nodes: AppNode[];
    links: AppLink[];
    customToolTemplates?: ToolTemplate[];
    activeLayer?: string;
  }
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'workspace');
  const payload: WorkspaceDocument = {
    ...data,
    updatedAt: new Date().toISOString(),
    updatedByUid: uid,
  };

  // 1. Update local cache
  try {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (_) {}

  // 2. Write to Firestore if authenticated
  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'state', 'workspace');
      await setDoc(docRef, {
        ...sanitizeForFirestore(payload),
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving workspace to Firestore:', error);
      return { success: false, error };
    }
  }

  return { success: true };
}

// ============================================================
// 4. CRM OPPORTUNITIES
// ============================================================

export async function loadProjectOpportunities(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: CrmOpportunity[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'crm');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'opportunities', 'all');
      const snap = await withTimeout(getDoc(docRef));
      if (snap && snap.exists() && snap.data()?.items) {
        const items = snap.data().items as CrmOpportunity[];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load CRM opportunities from Firestore, checking cache:', error);
    }
  }

  // Fallback: Local partitioned cache
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return { success: !remoteFailed, data: JSON.parse(cached) };
    }
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectOpportunities(
  uid: string,
  orgId: string,
  projectId: string,
  items: CrmOpportunity[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'crm');

  // Update local cache
  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  // Save to Firestore if authenticated
  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'opportunities', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        count: items.length,
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving CRM opportunities to Firestore:', error);
      return { success: false, error };
    }
  }

  return { success: true };
}

// ============================================================
// 5. STUDIO CONTENT (Owner Demo Only)
// ============================================================

export async function loadProjectStudio(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: ContentItem[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'studio');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'content', 'all');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data()?.items) {
        const items = snap.data().items as ContentItem[];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Studio content from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectStudio(
  uid: string,
  orgId: string,
  projectId: string,
  items: ContentItem[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'studio');

  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'content', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving Studio content to Firestore:', error);
      return { success: false, error };
    }
  }
  return { success: true };
}

// ============================================================
// 6. DIGITAL AI APPS (Owner Demo Only)
// ============================================================

export async function loadProjectAiApps(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: DigitalAiAsset[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'aiapps');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'aiApps', 'all');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data()?.items) {
        const items = sanitizeDigitalAiAssets(snap.data().items) || [];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load AI apps from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectAiApps(
  uid: string,
  orgId: string,
  projectId: string,
  items: DigitalAiAsset[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'aiapps');

  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'aiApps', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving AI Apps to Firestore:', error);
      return { success: false, error };
    }
  }
  return { success: true };
}

export async function loadProjectAiExecutionHistory(
  uid: string,
  orgId: string,
  projectId: string,
): Promise<{ success: boolean; data: TestExecutionResult[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'ai-history');
  let remoteFailed = false;
  if (auth.currentUser) {
    try {
      const historyRef = doc(db, 'organizations', orgId, 'projects', projectId, 'aiApps', 'execution-history');
      const snapshot = await getDoc(historyRef);
      if (snapshot.exists() && Array.isArray(snapshot.data()?.items)) {
        const items = snapshot.data().items as TestExecutionResult[];
        localStorage.setItem(cacheKey, JSON.stringify(items));
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load AI execution history from Firestore:', error);
    }
  }
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}
  return { success: !remoteFailed, data: null };
}

export async function saveProjectAiExecutionHistory(
  uid: string,
  orgId: string,
  projectId: string,
  items: TestExecutionResult[],
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'ai-history');
  localStorage.setItem(cacheKey, JSON.stringify(items));
  if (!auth.currentUser) return { success: true };
  try {
    const historyRef = doc(db, 'organizations', orgId, 'projects', projectId, 'aiApps', 'execution-history');
    await setDoc(historyRef, {
      items: sanitizeForFirestore(items.slice(0, 100)),
      updatedAt: new Date().toISOString(),
      updatedByUid: uid,
      serverTimestamp: serverTimestamp(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.warn('Warning saving AI execution history to Firestore:', error);
    return { success: false, error };
  }
}

// ============================================================
// 7. SPONSORS, ACTIVATIONS & LIBRARY PERSISTENCE
// ============================================================

export async function loadProjectSponsors(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: Sponsor[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'sponsors');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'sponsors', 'all');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data()?.items) {
        const items = snap.data().items as Sponsor[];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Sponsors from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectSponsors(
  uid: string,
  orgId: string,
  projectId: string,
  items: Sponsor[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'sponsors');

  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'sponsors', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving Sponsors to Firestore:', error);
      return { success: false, error };
    }
  }
  return { success: true };
}

export async function loadProjectSponsorOperations(
  uid: string,
  orgId: string,
  projectId: string,
): Promise<{ success: boolean; data: SponsorOperationsState | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'sponsor-operations-v2');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'sponsors', 'operations-v2');
      const snapshot = await withTimeout(getDoc(docRef));
      if (snapshot.exists() && snapshot.data()?.state) {
        const state = snapshot.data().state as SponsorOperationsState;
        try { localStorage.setItem(cacheKey, JSON.stringify(state)); } catch (_) {}
        return { success: true, data: state };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Sponsor Operations from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) as SponsorOperationsState };
  } catch (_) {}
  return { success: !remoteFailed, data: null };
}

export async function saveProjectSponsorOperations(
  uid: string,
  orgId: string,
  projectId: string,
  state: SponsorOperationsState,
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'sponsor-operations-v2');
  const payload: SponsorOperationsState = { ...state, version: 1, updatedAt: new Date().toISOString() };
  try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch (_) {}
  if (!auth.currentUser) return { success: true };

  try {
    const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'sponsors', 'operations-v2');
    await setDoc(docRef, {
      state: sanitizeForFirestore(payload),
      updatedAt: payload.updatedAt,
      updatedByUid: uid,
      serverTimestamp: serverTimestamp(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.warn('Warning saving Sponsor Operations to Firestore:', error);
    return { success: false, error };
  }
}

export async function loadProjectActivations(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: StageActivation[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'activations');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'activations', 'all');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data()?.items) {
        const items = snap.data().items as StageActivation[];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Activations from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectActivations(
  uid: string,
  orgId: string,
  projectId: string,
  items: StageActivation[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'activations');

  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'activations', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving Activations to Firestore:', error);
      return { success: false, error };
    }
  }
  return { success: true };
}

export async function loadProjectLibrary(
  uid: string,
  orgId: string,
  projectId: string
): Promise<{ success: boolean; data: LibraryItem[] | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'library');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'library', 'all');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data()?.items) {
        const items = snap.data().items as LibraryItem[];
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
        } catch (_) {}
        return { success: true, data: items };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Library from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectLibrary(
  uid: string,
  orgId: string,
  projectId: string,
  items: LibraryItem[]
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'library');

  try {
    localStorage.setItem(cacheKey, JSON.stringify(items));
  } catch (_) {}

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'library', 'all');
      await setDoc(docRef, {
        items: sanitizeForFirestore(items),
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.warn('Warning saving Library to Firestore:', error);
      return { success: false, error };
    }
  }
  return { success: true };
}

// ============================================================
// 8. EVENT OPERATIONS
// V2 uses a normalized aggregate document. The UI depends on this service,
// so the backing store can move to per-entity subcollections at higher scale.
// ============================================================

export async function loadProjectEventOperations(
  uid: string,
  orgId: string,
  projectId: string,
): Promise<{ success: boolean; data: EventOperationsState | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'event-operations');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'eventOperations', 'state');
      const snapshot = await withTimeout(getDoc(docRef));
      if (snapshot.exists() && snapshot.data()?.state) {
        const state = snapshot.data().state as EventOperationsState;
        try {
          localStorage.setItem(cacheKey, JSON.stringify(state));
        } catch (_) {}
        return { success: true, data: state };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load Event Operations from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) as EventOperationsState };
  } catch (_) {}

  return { success: !remoteFailed, data: null };
}

export async function saveProjectEventOperations(
  uid: string,
  orgId: string,
  projectId: string,
  state: EventOperationsState,
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'event-operations');
  const payload: EventOperationsState = {
    ...state,
    version: 2,
    updatedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (_) {}

  if (!auth.currentUser) return { success: true };
  try {
    const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'eventOperations', 'state');
    await setDoc(docRef, {
      state: sanitizeForFirestore(payload),
      updatedAt: payload.updatedAt,
      updatedByUid: uid,
      serverTimestamp: serverTimestamp(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.warn('Warning saving Event Operations to Firestore:', error);
    return { success: false, error };
  }
}

export async function loadProjectEventPortfolio(
  uid: string,
  orgId: string,
  projectId: string,
): Promise<{ success: boolean; data: EventPortfolioState | null; legacyEvent?: EventOperationsState | null }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'event-portfolio');
  let remoteFailed = false;

  if (auth.currentUser) {
    try {
      const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'eventOperations', 'portfolio');
      const snapshot = await withTimeout(getDoc(docRef));
      if (snapshot.exists() && snapshot.data()?.portfolio) {
        const portfolio = snapshot.data().portfolio as EventPortfolioState;
        try { localStorage.setItem(cacheKey, JSON.stringify(portfolio)); } catch (_) {}
        return { success: true, data: portfolio };
      }
    } catch (error) {
      remoteFailed = true;
      console.warn('Could not load the event portfolio from Firestore:', error);
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return { success: !remoteFailed, data: JSON.parse(cached) as EventPortfolioState };
  } catch (_) {}

  const legacy = await loadProjectEventOperations(uid, orgId, projectId);
  return { success: !remoteFailed && legacy.success, data: null, legacyEvent: legacy.data };
}

export async function saveProjectEventPortfolio(
  uid: string,
  orgId: string,
  projectId: string,
  portfolio: EventPortfolioState,
): Promise<{ success: boolean; error?: unknown }> {
  const cacheKey = getLocalCacheKey(uid, orgId, projectId, 'event-portfolio');
  const payload = sanitizeForFirestore<EventPortfolioState>({
    ...portfolio,
    version: 1,
    updatedAt: new Date().toISOString(),
  });
  try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch (_) {}
  if (!auth.currentUser) return { success: true };
  try {
    const docRef = doc(db, 'organizations', orgId, 'projects', projectId, 'eventOperations', 'portfolio');
    await setDoc(docRef, {
      portfolio: payload,
      updatedAt: payload.updatedAt,
      updatedByUid: uid,
      serverTimestamp: serverTimestamp(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.warn('Warning saving the event portfolio to Firestore:', error);
    return { success: false, error };
  }
}

// ============================================================
// PUBLIC OFFER PAGES
// ============================================================

const publicOfferCacheKey = (slug: string) => `grosym_public_offer_${slug}`;

export async function publishPublicOffer(
  offer: PublicOfferDocument,
): Promise<{ success: boolean; localOnly?: boolean; error?: unknown }> {
  if (!auth.currentUser) return { success: false, error: new Error('PUBLIC_OFFER_AUTH_REQUIRED') };
  const offerRef = doc(db, 'publicOffers', offer.slug);

  try {
    const existing = await withTimeout(getDoc(offerRef));
    if (existing.exists()) {
      const current = existing.data() as Partial<PublicOfferDocument>;
      const belongsToCurrentOffer = current.organizationId === offer.organizationId
        && current.projectId === offer.projectId
        && current.eventId === offer.eventId;
      if (!belongsToCurrentOffer) {
        return { success: false, error: new Error('PUBLIC_OFFER_SLUG_TAKEN') };
      }
    }

    await setDoc(offerRef, {
      ...sanitizeForFirestore(offer),
      serverTimestamp: serverTimestamp(),
      publishedByUid: auth.currentUser.uid,
    });
    try { localStorage.setItem(publicOfferCacheKey(offer.slug), JSON.stringify(offer)); } catch (_) {}
    return { success: true };
  } catch (error) {
    const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (isLocal) {
      try {
        localStorage.setItem(publicOfferCacheKey(offer.slug), JSON.stringify(offer));
        console.warn('Public offer stored locally because Firestore publication is unavailable:', error);
        return { success: true, localOnly: true, error };
      } catch (_) {}
    }
    console.warn('Could not publish the public offer:', error);
    return { success: false, error };
  }
}

export async function loadPublicOfferBySlug(slug: string): Promise<PublicOfferDocument | null> {
  try {
    const snapshot = await withTimeout(getDoc(doc(db, 'publicOffers', slug)));
    if (snapshot.exists() && snapshot.data()?.status === 'published') {
      return snapshot.data() as PublicOfferDocument;
    }
  } catch (error) {
    console.warn('Could not load the public offer from Firestore:', error);
  }

  const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isLocal) {
    try {
      const cached = localStorage.getItem(publicOfferCacheKey(slug));
      if (cached) {
        const offer = JSON.parse(cached) as PublicOfferDocument;
        return offer.status === 'published' ? offer : null;
      }
    } catch (_) {}
  }
  return null;
}

export async function unpublishPublicOffer(
  slug: string,
  organizationId: string,
  projectId: string,
  eventId: string,
): Promise<{ success: boolean; localOnly?: boolean; error?: unknown }> {
  if (!auth.currentUser) return { success: false, error: new Error('PUBLIC_OFFER_AUTH_REQUIRED') };
  const offerRef = doc(db, 'publicOffers', slug);
  try {
    const existing = await withTimeout(getDoc(offerRef));
    if (existing.exists()) {
      const current = existing.data() as Partial<PublicOfferDocument>;
      const belongsToCurrentOffer = current.organizationId === organizationId
        && current.projectId === projectId
        && current.eventId === eventId;
      if (!belongsToCurrentOffer) return { success: false, error: new Error('PUBLIC_OFFER_OWNERSHIP_MISMATCH') };
      await deleteDoc(offerRef);
    }
    try { localStorage.removeItem(publicOfferCacheKey(slug)); } catch (_) {}
    return { success: true };
  } catch (error) {
    const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (isLocal) {
      try {
        localStorage.removeItem(publicOfferCacheKey(slug));
        return { success: true, localOnly: true, error };
      } catch (_) {}
    }
    return { success: false, error };
  }
}

// ============================================================
// 9. LEGACY DATA MIGRATION (Owner Explicit Confirmation Only)
// ============================================================

/**
 * Checks if previous unpartitioned localStorage data exists in the user's browser
 */
export function checkLegacyLocalStorage(): LegacyMigrationData {
  if (localStorage.getItem('grosym_legacy_migrated_v1') === 'true') {
    return { hasLegacyData: false, detectedItems: { customClientsCount: 0, opportunitiesCount: 0, studioCount: 0, digitalAiCount: 0 } };
  }

  let customClientsCount = 0;
  let opportunitiesCount = 0;
  let studioCount = 0;
  let digitalAiCount = 0;

  try {
    const custom = localStorage.getItem('grosym_custom_clients');
    if (custom) {
      const parsed = JSON.parse(custom);
      if (Array.isArray(parsed)) customClientsCount = parsed.length;
    }

    const opps = localStorage.getItem('grosym_crm_opportunities');
    if (opps) {
      const parsed = JSON.parse(opps);
      if (Array.isArray(parsed)) opportunitiesCount = parsed.length;
    }

    const studio = localStorage.getItem('grosym_studio_content');
    if (studio) {
      const parsed = JSON.parse(studio);
      if (Array.isArray(parsed)) studioCount = parsed.length;
    }

    const ai = localStorage.getItem('grosym_digital_ai_assets') || localStorage.getItem('grosym_digital_ai_apps');
    if (ai) {
      const parsed = JSON.parse(ai);
      if (Array.isArray(parsed)) digitalAiCount = parsed.length;
    }
  } catch (_) {}

  const hasLegacyData = (customClientsCount + opportunitiesCount + studioCount + digitalAiCount) > 0;

  return {
    hasLegacyData,
    detectedItems: {
      customClientsCount,
      opportunitiesCount,
      studioCount,
      digitalAiCount,
    }
  };
}

/**
 * Performs backup and safely imports legacy data into the current tenant organization and project.
 * Requires Owner role and explicit user trigger.
 */
export async function executeLegacyMigration(
  uid: string,
  orgId: string,
  projectId: string,
  userRole: UserRole
): Promise<{ success: boolean; countImported: number; countFailed: number; backupKey: string; message: string }> {
  if (userRole !== 'owner') {
    return {
      success: false,
      countImported: 0,
      countFailed: 0,
      backupKey: '',
      message: 'Solo los usuarios con rol Owner pueden ejecutar la migración de datos.',
    };
  }

  const backupKey = `grosym_backup_pre_migration_${Date.now()}`;
  let countImported = 0;
  let countFailed = 0;

  // 1. Create a safe raw JSON snapshot backup of all existing localStorage keys
  const backupObject: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('grosym_')) {
      backupObject[k] = localStorage.getItem(k) || '';
    }
  }
  try {
    localStorage.setItem(backupKey, JSON.stringify(backupObject));
  } catch (_) {}

  // 2. Import opportunities if present
  try {
    const rawOpps = localStorage.getItem('grosym_crm_opportunities');
    if (rawOpps) {
      const parsed = JSON.parse(rawOpps);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const res = await saveProjectOpportunities(uid, orgId, projectId, parsed);
        if (res.success) {
          countImported += parsed.length;
        } else {
          countFailed += parsed.length;
        }
      }
    }
  } catch (e) {
    console.warn('Error migrating legacy opportunities:', e);
    countFailed++;
  }

  // 3. Import studio content if present
  try {
    const rawStudio = localStorage.getItem('grosym_studio_content');
    if (rawStudio) {
      const parsed = JSON.parse(rawStudio);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const res = await saveProjectStudio(uid, orgId, projectId, parsed);
        if (res.success) {
          countImported += parsed.length;
        } else {
          countFailed += parsed.length;
        }
      }
    }
  } catch (e) {
    console.warn('Error migrating legacy studio content:', e);
    countFailed++;
  }

  // 4. Import digital AI apps if present
  try {
    const rawAi = localStorage.getItem('grosym_digital_ai_assets') || localStorage.getItem('grosym_digital_ai_apps');
    if (rawAi) {
      const parsed = JSON.parse(rawAi);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const sanitized = sanitizeDigitalAiAssets(parsed) || [];
        const res = await saveProjectAiApps(uid, orgId, projectId, sanitized);
        if (res.success) {
          countImported += parsed.length;
        } else {
          countFailed += parsed.length;
        }
      }
    }
  } catch (e) {
    console.warn('Error migrating legacy AI apps:', e);
    countFailed++;
  }

  if (countFailed === 0) {
    localStorage.setItem('grosym_legacy_migrated_v1', 'true');
    localStorage.setItem('grosym_last_backup_key', backupKey);
    return {
      success: true,
      countImported,
      countFailed,
      backupKey,
      message: `Migración completada con éxito: ${countImported} registros guardados en Firestore. Instantánea de seguridad guardada en ${backupKey}.`,
    };
  } else {
    return {
      success: false,
      countImported,
      countFailed,
      backupKey,
      message: `Migración parcial: ${countImported} registros guardados, ${countFailed} escrituras fallaron. No se marcó como completada.`,
    };
  }
}

// ============================================================
// 8. ONBOARDING & USER CUSTOM FLOW (LIENZO 2) PERSISTENCE
// ============================================================

/**
 * Persists onboarding data and the custom generated Lienzo 2 to Firestore and local cache
 */
export async function saveUserOnboardingData(
  uid: string,
  orgId: string,
  answers: OnboardingAnswers,
  customFlow: PresetFlow
): Promise<{ success: boolean; error?: any }> {
  const localKey = `grosym_onboarding_${uid}`;
  const customFlowKey = `grosym_custom_flow_${uid}`;

  try {
    localStorage.setItem(localKey, JSON.stringify(answers));
    localStorage.setItem(customFlowKey, JSON.stringify(customFlow));
  } catch (_) {}

  if (auth.currentUser && uid && uid !== 'guest_user') {
    try {
      // 1. Update user profile document in Firestore
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        name: answers.userName,
        userAge: answers.userAge,
        email: answers.email,
        companyName: answers.projectName || answers.companyName,
        industry: answers.industry,
        primaryGoal: answers.primaryGoal,
        ticketPrice: answers.ticketPrice,
        monthlyAdBudgetUSD: answers.monthlyAdBudgetUSD,
        selectedTools: answers.selectedTools,
        hasCompletedOnboarding: true,
        onboardingCompletedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // 2. Save custom flow as project workspace in Firestore
      const projId = 'proj_custom_user';
      const workspaceRef = doc(db, 'organizations', orgId || 'org_grosym_growth', 'projects', projId, 'state', 'workspace');
      await setDoc(workspaceRef, {
        parameters: customFlow.parameters,
        nodes: customFlow.nodes,
        links: customFlow.links,
        activeLayer: 'global',
        updatedAt: new Date().toISOString(),
        updatedByUid: uid,
        serverTimestamp: serverTimestamp(),
      }, { merge: true });

      // 3. Save project metadata in Firestore
      const projMetaRef = doc(db, 'organizations', orgId || 'org_grosym_growth', 'projects', projId);
      await setDoc(projMetaRef, {
        id: projId,
        name: customFlow.name,
        description: customFlow.description,
        industry: customFlow.industry,
        organizationId: orgId || 'org_grosym_growth',
        isDefault: false,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.warn('Warning saving onboarding data to Firestore:', error);
      return { success: false, error };
    }
  }

  return { success: true };
}

/**
 * Loads the user's custom generated Lienzo 2 if available
 */
export async function loadUserCustomFlow(
  uid: string,
  orgId: string
): Promise<PresetFlow | null> {
  const customFlowKey = `grosym_custom_flow_${uid}`;

  // Check local cache first
  try {
    const cached = localStorage.getItem(customFlowKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.name && Array.isArray(parsed.nodes)) {
        return parsed as PresetFlow;
      }
    }
  } catch (_) {}

  // Fetch from Firestore
  if (auth.currentUser && uid && uid !== 'guest_user') {
    try {
      const projId = 'proj_custom_user';
      const workspaceRef = doc(db, 'organizations', orgId || 'org_grosym_growth', 'projects', projId, 'state', 'workspace');
      const snap = await withTimeout(getDoc(workspaceRef), 3000);
      if (snap.exists()) {
        const data = snap.data();
        const projMetaRef = doc(db, 'organizations', orgId || 'org_grosym_growth', 'projects', projId);
        const metaSnap = await withTimeout(getDoc(projMetaRef), 3000);
        const metaData = metaSnap.exists() ? metaSnap.data() : {};

        const customFlow: PresetFlow = {
          name: metaData.name || 'Mi Estrategia Personalizada',
          description: metaData.description || 'Lienzo personalizado creado mediante Onboarding.',
          parameters: data.parameters,
          nodes: data.nodes || [],
          links: data.links || [],
          industry: metaData.industry
        };

        try {
          localStorage.setItem(customFlowKey, JSON.stringify(customFlow));
        } catch (_) {}

        return customFlow;
      }
    } catch (e) {
      console.warn('Could not load custom flow from Firestore:', e);
    }
  }

  return null;
}

